import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, oneOfType, shape, string } from 'prop-types'
import { forwardRef } from 'react'

import { isMobile } from 'shared/utils/deviceClassUtils'
import noop from 'shared/utils/noop'
import HealthServices from 'views/components/organisms/HeaderNew/HealthServices'
import Products from 'views/components/organisms/HeaderNew/Products'
import RedeemRequestPrescription from 'views/components/organisms/HeaderNew/RedeemRequestPrescription'
import YourBenefits from 'views/components/organisms/HeaderNew/YourBenefits'
import OffCanvasHomeView from 'views/components/organisms/OffCanvasNew/OffCanvasHomeView'
import OffCanvasNewScreen from 'views/components/organisms/OffCanvasNew/OffCanvasNewScreen'
import { offCanvasCategory, shopLanguagesType } from 'views/utils/propTypes'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

import './OffCanvasNew.scss'

const staticPagesMap = {
  // eslint-disable-next-line react/prop-types
  'allProducts': ({ menuLinks }) => (
    // eslint-disable-next-line react/prop-types
    <Products menuLinks={menuLinks.products} />
  ),
  'redeemRecipe': () => <RedeemRequestPrescription />,
  // eslint-disable-next-line react/prop-types
  'healthServices': ({ menuLinks }) => (
    // eslint-disable-next-line react/prop-types
    <HealthServices menuLinks={menuLinks.healthServices} />
  ),
  // eslint-disable-next-line react/prop-types
  'yourAdvantages': ({ menuLinks }) => (
    // eslint-disable-next-line react/prop-types
    <YourBenefits menuLinks={menuLinks.yourBenefits} />
  ),
}

const OffCanvasNew = forwardRef(function OffCanvasNew(props, ref) {
  const {
    visibleStack,
    hidden,
    goBackHandler,
    closeMenuHandler,
    goNextHandler,
    language,
    userData,
    onShowStaticPage,
    menuLinks,
  } = props

  const { deviceClass } = usePublicConfig()
  const navClass = classNames('u-hidden@print o-OffCanvas', {
    'u-hidden@medium': !isMobile(deviceClass),
  })

  const handleShowStaticPage = view => {
    onShowStaticPage && onShowStaticPage({ viewType: 'static', ...view })
  }

  return (
    <nav
      id="OffCanvasMenu"
      className={navClass}
      aria-hidden={hidden ? true : null}
      aria-labelledby="OffCanvasMenuHeader"
      data-clientside-hook="menu-slideover"
      ref={ref}
    >
      {visibleStack.map((view, i) => {
        const commonProps = {
          key: `${view.id}-${i}`,
          data: view,
          closeMenuHandler,
          goNextHandler,
          goBackHandler,
          onShowStaticPage,
          language,
          userData,
          dataQaId: `m-${view.id}`,
        }

        const mainView = (
          <OffCanvasHomeView
            {...commonProps}
            onShowStaticPage={handleShowStaticPage}
          />
        )

        if (i === 0) {
          return mainView
        }

        if (view?.viewType === 'static' && staticPagesMap[view.id]) {
          return (
            <OffCanvasNewScreen
              key={view.id}
              headerInfo={<Trans id={view.label} />}
              onMenuClose={closeMenuHandler}
              onGoBack={goBackHandler}
              backButtonText={<Trans id="offcanvas.header.backToMain" />}
              style={view.style}
              dataQaId={`m-${view.id}`}
            >
              {staticPagesMap[view.id]({ menuLinks })}
            </OffCanvasNewScreen>
          )
        }

        return mainView
      })}
    </nav>
  )
})

OffCanvasNew.propTypes = {
  menuLinks: shape({}).isRequired,
  visibleStack: arrayOf(offCanvasCategory).isRequired,
  userData: shape({}),
  language: oneOfType([string, shopLanguagesType]),
  hidden: bool,
  goBackHandler: func,
  closeMenuHandler: func,
  goNextHandler: func,
  onShowStaticPage: func,
}

OffCanvasNew.defaultProps = {
  userData: null,
  language: null,
  hidden: false,
  goBackHandler: noop,
  closeMenuHandler: noop,
  goNextHandler: () => noop,
  onShowStaticPage: noop,
}

export default OffCanvasNew
